import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Check, Play } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useCart } from "../context/CartContext";
import { MediaEmbed } from "../components/MediaEmbed";

export default function ProductDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { addItem } = useCart();
  const [p, setP] = useState(null);
  const [size, setSize] = useState("");
  const [color, setColor] = useState("");
  const [qty, setQty] = useState(1);
  const [activeIdx, setActiveIdx] = useState(0);
  const [zoom, setZoom] = useState({ active: false, x: "50%", y: "50%" });

  const onZoomMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - r.left) / r.width) * 100;
    const y = ((e.clientY - r.top) / r.height) * 100;
    setZoom({ active: true, x: `${x}%`, y: `${y}%` });
  };

  useEffect(() => {
    api.get(`/products/${id}`).then((r) => {
      setP(r.data);
      setActiveIdx(0);
      if (r.data.sizes?.length) setSize(r.data.sizes[0]);
      if (r.data.colors?.length) setColor(r.data.colors[0]);
    });
  }, [id]);

  if (!p) return <div className="max-w-[1400px] mx-auto px-5 py-20 font-mono">Loading_</div>;

  const isPrint = p.type === "print";
  const gallery = (p.images && p.images.length) ? p.images : [p.image];
  const media = [
    ...gallery.map((u) => ({ type: "image", url: u })),
    ...(p.video ? [{ type: "video", url: p.video }] : []),
  ];
  const active = media[activeIdx] || media[0];

  const handleAdd = () => {
    if (p.sizes?.length && !size) return toast.error("Select a size");
    addItem({ product_id: p.id, name: p.name, image: p.image, price: p.price, qty, size, color, type: p.type });
    toast.success(`${p.name} added to cart`);
  };

  return (
    <div className="max-w-[1400px] mx-auto px-5 md:px-8 py-8 md:py-12" data-testid="product-detail-page">
      <button onClick={() => nav(-1)} className="font-mono text-xs uppercase tracking-widest flex items-center gap-2 mb-6 hover:text-primary">
        <ArrowLeft size={16} /> Back
      </button>
      <div className="grid md:grid-cols-2 gap-0 border-2 border-border">
        <div className="border-b-2 md:border-b-0 md:border-r-2 border-border bg-muted">
          {active?.type === "video" ? (
            <div className="aspect-square bg-black" data-testid="product-video">
              <MediaEmbed url={active.url} className="w-full h-full object-cover" controls />
            </div>
          ) : (
            <div
              className="overflow-hidden cursor-zoom-in aspect-square"
              onMouseMove={onZoomMove}
              onMouseLeave={() => setZoom((z) => ({ ...z, active: false }))}
              data-testid="product-image-zoom"
            >
              <img
                src={active?.url || p.image}
                alt={p.name}
                className="w-full h-full object-cover"
                data-testid="product-image"
                style={{
                  transform: zoom.active ? "scale(2.2)" : "scale(1)",
                  transformOrigin: `${zoom.x} ${zoom.y}`,
                  transition: "transform 0.15s ease-out",
                }}
              />
            </div>
          )}
          {media.length > 1 && (
            <div className="flex gap-2 p-3 flex-wrap border-t-2 border-border" data-testid="product-thumbnails">
              {media.map((m, idx) => (
                <button
                  key={idx}
                  onClick={() => setActiveIdx(idx)}
                  data-testid={`product-thumb-${idx}`}
                  className={`relative w-16 h-16 border-2 overflow-hidden ${activeIdx === idx ? "border-primary" : "border-border"}`}
                >
                  {m.type === "video" ? (
                    <>
                      <video src={m.url} className="w-full h-full object-cover" muted preload="metadata" />
                      <span className="absolute inset-0 flex items-center justify-center bg-black/40"><Play size={16} className="text-white" fill="white" /></span>
                    </>
                  ) : (
                    <img src={m.url} alt={`${p.name}-${idx}`} className="w-full h-full object-cover" />
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="p-6 md:p-10">
          <div className="flex gap-2 mb-4">
            <span className="bg-black text-white font-mono text-[10px] uppercase tracking-widest px-2 py-1">{isPrint ? "Print" : "T-Shirt"}</span>
            <span className="bg-primary text-primary-foreground font-mono text-[10px] uppercase tracking-widest px-2 py-1">{p.category}</span>
          </div>
          <h1 className="font-anton text-4xl md:text-5xl uppercase tracking-tighter leading-none" data-testid="product-name">{p.name}</h1>
          <p className="font-anton text-3xl mt-4 text-primary" data-testid="product-price">${p.price.toFixed(2)}</p>
          <p className="font-mono text-sm text-muted-foreground mt-5 leading-relaxed">{p.description}</p>

          {isPrint && (
            <div className="mt-5 border-2 border-border bg-muted p-3 font-mono text-xs uppercase tracking-widest flex items-center gap-2">
              <Check size={14} className="text-primary" /> We print it & mail it to your door
            </div>
          )}

          {p.sizes?.length > 0 && (
            <div className="mt-7">
              <h4 className="font-outfit font-bold uppercase text-xs tracking-widest mb-3">{isPrint ? "Size" : "Pick Your Size"}</h4>
              <div className="flex flex-wrap gap-2">
                {p.sizes.map((s) => (
                  <button
                    key={s}
                    data-testid={`size-${s}`}
                    onClick={() => setSize(s)}
                    className={`w-12 h-12 border-2 border-border font-mono text-sm font-bold transition-colors ${size === s ? "bg-black text-white" : "bg-card hover:bg-muted"}`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          {p.colors?.length > 0 && (
            <div className="mt-6">
              <h4 className="font-outfit font-bold uppercase text-xs tracking-widest mb-3">Color</h4>
              <div className="flex flex-wrap gap-2">
                {p.colors.map((c) => (
                  <button
                    key={c}
                    data-testid={`color-${c}`}
                    onClick={() => setColor(c)}
                    className={`px-4 h-10 border-2 border-border font-mono text-xs uppercase transition-colors ${color === c ? "bg-primary text-primary-foreground border-primary" : "bg-card hover:bg-muted"}`}
                  >
                    {c}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="mt-7 flex items-center gap-4">
            <div className="flex border-2 border-border">
              <button onClick={() => setQty(Math.max(1, qty - 1))} data-testid="qty-minus" className="w-10 h-12 font-bold hover:bg-muted">−</button>
              <span className="w-12 h-12 flex items-center justify-center font-mono font-bold border-x-2 border-border" data-testid="qty-value">{qty}</span>
              <button onClick={() => setQty(qty + 1)} data-testid="qty-plus" className="w-10 h-12 font-bold hover:bg-muted">+</button>
            </div>
          </div>

          <button
            onClick={handleAdd}
            data-testid="add-to-cart-btn"
            className="mt-7 w-full bg-primary text-primary-foreground font-outfit font-bold uppercase tracking-widest text-sm py-5 border-2 border-border hover:bg-secondary hover:border-secondary hover:text-white transition-colors"
          >
            Add To Cart — ${(p.price * qty).toFixed(2)}
          </button>
        </div>
      </div>
    </div>
  );
}
